// Plan madde 10.3 — render-time role guard.
//
// Wraps children; renders them only when the current operator's role
// list satisfies the required role(s). Three fallback states:
//   * loading — useOperatorMe is fetching → renders `loading` prop or null
//   * unauthorized — session loaded but role mismatch → renders `fallback` prop or null
//   * authenticated + match — renders children
//
// Usage patterns:
//   1. Single role gate:
//        <RequireRole requiredRole="role_op_admin"><IamPanel /></RequireRole>
//   2. Any-of-list gate:
//        <RequireRole requiredRole={["role_op_finance", "role_op_admin"]}><RefundForm /></RequireRole>
//   3. Action-based gate (preferred — centralizes policy):
//        <RequireAction action="billing.refund"><RefundForm /></RequireAction>
//
// Backend STILL enforces — RequireRole is a UX optimization. A user who
// bypasses (e.g., DevTools removes the wrapper) hits 403 at the route
// handler's requireOperatorRole() middleware. NEVER use this as a
// security boundary.

import type { ReactNode } from "react";

import {
  hasAnyRole,
  isPermitted,
  type OperatorAction,
  type OperatorRole,
} from "@/lib/operator-permissions";

import { useOperatorMe } from "@/hooks/useOperatorMe";

interface RequireRoleProps {
  /**
   * Single required role, or an array — operator needs AT LEAST ONE.
   * Named `requiredRole` rather than `role` to sidestep the
   * jsx-a11y/aria-role lint rule (which treats any prop named `role`
   * as an HTML aria-role attribute and flags non-ARIA values like
   * "role_op_admin"). Also more self-documenting at callsites.
   */
  readonly requiredRole: OperatorRole | readonly OperatorRole[];
  /** Rendered while useOperatorMe is loading. Default: null (invisible). */
  readonly loading?: ReactNode;
  /** Rendered when session loaded but role mismatch. Default: null. */
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

export function RequireRole(props: RequireRoleProps): ReactNode {
  const me = useOperatorMe();

  if (me.isLoading) {
    return props.loading ?? null;
  }
  if (me.data === undefined) {
    return props.fallback ?? null;
  }

  const required = Array.isArray(props.requiredRole) ? props.requiredRole : [props.requiredRole];
  const allowed = hasAnyRole(required as readonly OperatorRole[], me.data.roles);
  return allowed ? props.children : (props.fallback ?? null);
}

interface RequireActionProps {
  readonly action: OperatorAction;
  readonly loading?: ReactNode;
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

/**
 * Action-based variant — preferred over <RequireRole> when a permission
 * is in the OPERATOR_ACTIONS map (i.e., maps to a backend route's
 * requireOperatorRole call). Centralizes role lists in one file
 * (lib/operator-permissions.ts) instead of scattering literal role
 * arrays across components.
 */
export function RequireAction(props: RequireActionProps): ReactNode {
  const me = useOperatorMe();

  if (me.isLoading) {
    return props.loading ?? null;
  }
  if (me.data === undefined) {
    return props.fallback ?? null;
  }

  const allowed = isPermitted(props.action, me.data.roles);
  return allowed ? props.children : (props.fallback ?? null);
}
